import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { alertsApi } from '@/lib/api/client';
import type { AlertRule } from '@steward/shared';
import toast from 'react-hot-toast';

export const alertKeys = {
  all: ['alerts'] as const,
  rules: () => [...alertKeys.all, 'rules'] as const,
  history: () => [...alertKeys.all, 'history'] as const,
  historyList: (filters: { unreadOnly?: boolean }) => [...alertKeys.history(), filters] as const,
  unreadCount: () => [...alertKeys.all, 'unread-count'] as const,
};

export function useAlertRules() {
  return useQuery({
    queryKey: alertKeys.rules(),
    queryFn: () => alertsApi.listRules(),
    select: (data) => data.rules,
  });
}

export function useCreateAlertRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Omit<AlertRule, 'id' | 'userId' | 'createdAt' | 'updatedAt' | 'lastTriggeredAt'>) =>
      alertsApi.createRule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: alertKeys.rules() });
      toast.success('Alert rule created');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create alert rule');
    },
  });
}

export function useUpdateAlertRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AlertRule> }) =>
      alertsApi.updateRule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: alertKeys.rules() });
      toast.success('Alert rule updated');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update alert rule');
    },
  });
}

export function useDeleteAlertRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => alertsApi.deleteRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: alertKeys.rules() });
      toast.success('Alert rule deleted');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete alert rule');
    },
  });
}

export function useAlertHistory(params?: { unreadOnly?: boolean; limit?: number }) {
  return useQuery({
    queryKey: alertKeys.historyList(params || {}),
    queryFn: () => alertsApi.listHistory(params),
    select: (data) => data.alerts,
  });
}

export function useUnreadAlertCount() {
  return useQuery({
    queryKey: alertKeys.unreadCount(),
    queryFn: () => alertsApi.unreadCount(),
    select: (data) => data.count,
    refetchInterval: 60_000, // Poll every 60 seconds
  });
}

export function useMarkAlertRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => alertsApi.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: alertKeys.history() });
      queryClient.invalidateQueries({ queryKey: alertKeys.unreadCount() });
    },
  });
}

export function useMarkAllAlertsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => alertsApi.markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: alertKeys.history() });
      queryClient.invalidateQueries({ queryKey: alertKeys.unreadCount() });
      toast.success('All alerts marked as read');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to mark alerts as read');
    },
  });
}
